import { useReveal } from "../ui/useReveal";

/**
 * The landing page — everything a signed-out visitor sees.
 *
 * The app asks for a sign-in before showing anything, so this page does all of
 * the selling. The rules that shaped almost every line:
 *
 *  1. NEVER SAY "AI". The penalty for that phrase is steepest in high-risk
 *     categories, and food someone will eat sits near medical devices on that
 *     scale. Yuka — the closest product to this one — uses the word zero times
 *     and says "deciphers" instead. The model is named in the FAQ, where
 *     disclosure builds trust rather than spending it.
 *
 *  2. GUILT, NEVER SHAME. Shame attaches to a person and drives concealment;
 *     guilt attaches to a behaviour and drives repair. "You throw away" must
 *     never stand without "you forgot" arriving in the same breath — it blames
 *     a circumstance rather than a character.
 *
 *  3. SELL THE OUTCOME, NOT THE MECHANISM. How it works is what the app is for.
 *
 *  4. WRITTEN FOR WHOEVER ACTUALLY RUNS THE KITCHEN, and never says so. The
 *     page speaks to them as "you" and names no category and no gender.
 *     Labelling an audience describes them to themselves, which reads as
 *     marketing rather than recognition — and quietly tells everyone else in
 *     the same kitchen that the app is not for them.
 *
 *  5. NO CLAIM THE DATA DOESN'T CARRY. The illness figures are real, sourced,
 *     and describe UNSAFE FOOD BROADLY — pathogens, bad water, adulteration.
 *     They are NOT a count of people harmed by eating something past its date.
 *     The line under them narrows to what is actually true of a home fridge.
 *     Do not "tighten" that into implying Larder prevents 100 million illnesses.
 *
 *  6. SHORT. An earlier version argued its case in full paragraphs and read
 *     like an essay written to convince its author. Nobody reads a landing page
 *     that way — they skim headings, look at pictures, and decide. Every
 *     section is now a heading, one idea, and out. If a line needs a second
 *     sentence to survive, the first sentence is usually the problem.
 */
export function Landing({ onStart, busy }: { onStart: () => void; busy: boolean }) {
  useReveal();

  const cta = (
    <button
      type="button"
      className="primary"
      onClick={onStart}
      disabled={busy}
      data-testid="get-started"
    >
      {busy ? "One moment…" : "Get started"}
    </button>
  );

  return (
    <div className="landing">
      <header className="land-top">
        <span className="brandmark">🧺 Larder</span>
      </header>

      <section className="land-hero">
        <div className="land-hero-copy">
          <h1>
            Ever bought food just to <em>let it expire?</em>
          </h1>
          <p className="land-lede">
            Larder remembers what's in your kitchen and tells you what to eat first.
          </p>
          {cta}
          <p className="land-trust">Free. No ads.</p>
        </div>
      </section>

      <section className="land-block" data-reveal>
        <img
          className="land-art"
          src="/art/forgotten.webp"
          alt="The same bunch of coriander twice: fresh and green, then dried out and brown"
          width={1472}
          height={981}
          loading="lazy"
          decoding="async"
        />
        <div className="land-copy">
          <h2>You didn't waste it. You forgot it was there.</h2>
          <p>
            It was fine on Tuesday. By Friday it wasn't, and the drawer never opened in
            between.
          </p>
        </div>
      </section>

      <section className="land-block" data-reveal>
        <h2>Running a house is a real job</h2>
        <p>
          You already hold a hundred things in your head. Nobody hands you a list — you{" "}
          <em>are</em> the list.
        </p>
        <p className="land-note">Remembering is the one part a phone can take off you.</p>
      </section>

      {/* The numbers as FIGURES, not prose. Three paragraphs of careful nuance
          about what these statistics do and don't cover was the most skippable
          block on the page. The qualifier survives as one line, because it is
          the part that keeps the claim honest — see rule 5. */}
      <section className="land-block land-risk" data-reveal>
        <h2>And some of it doesn't just go to waste</h2>
        <dl className="land-stats">
          <div>
            <dt>150m</dt>
            <dd>fall ill a year from unsafe food in South-East Asia</dd>
          </div>
          <div>
            <dt>175k</dt>
            <dd>die of it in the same year</dd>
          </div>
          <div>
            <dt>100m</dt>
            <dd>of those cases estimated in India alone</dd>
          </div>
        </dl>
        <p className="land-fine">
          World Health Organization. Mostly contamination and unsafe water — not a biscuit
          a week past its date.
        </p>
        <p>
          But the risk in <em>your</em> fridge is short and specific: milk, curd, paneer,
          fish, last night's food. Those don't go stale. They go bad, and they look fine
          doing it.
        </p>
      </section>

      <section className="land-block" data-reveal>
        <img
          className="land-art"
          src="/art/cooked.webp"
          alt="A tomato lifting off a kitchen shelf towards a warm, lit pan"
          width={1472}
          height={981}
          loading="lazy"
          decoding="async"
        />
        <div className="land-copy">
          <h2>What changes</h2>
          <ul className="land-gets">
            <li>
              <strong>You know what's in the house</strong> without opening anything.
            </li>
            <li>
              <strong>Nothing quietly goes off</strong> — you hear while you can still eat
              it.
            </li>
            <li>
              <strong>You stop buying the third bottle</strong> of what you already have.
            </li>
            <li>
              <strong>You throw away less money</strong> every month.
            </li>
          </ul>
        </div>
      </section>

      <section className="land-block" data-reveal>
        <h2>Free, and quiet about you</h2>
        <p>
          No ads, nothing sold. A phone number or an account name is all Larder knows
          about you — delete it and it's gone.
        </p>
      </section>

      <section className="land-block" data-reveal>
        <h2>Common questions</h2>

        <details className="faq">
          <summary>Is it really free?</summary>
          <p>Yes. No ads, no subscription, nothing to buy.</p>
        </details>

        <details className="faq">
          <summary>How do I sign in?</summary>
          <p>A phone number, Google, or Apple. A phone number takes one code by SMS.</p>
        </details>

        <details className="faq">
          <summary>What reads the labels?</summary>
          <p>
            Photographs go to Google's Gemini, which sends back a name and a date. Nothing
            else goes with them, and Larder doesn't keep the picture. If a date can't be
            read confidently you get no date rather than a wrong one — a missing date
            costs you one tap, a wrong one makes you bin something that was fine.
          </p>
        </details>

        <details className="faq">
          <summary>Who can see my list?</summary>
          <p>
            You. It's tied to your account, and every other account is refused at the
            database itself, not just in the app.
          </p>
        </details>

        <details className="faq">
          <summary>Does it work for things that don't expire?</summary>
          <p>
            Yes. Salt, shampoo, a book — Larder remembers where you put it and never nags
            you about it.
          </p>
        </details>

        <details className="faq">
          <summary>Do I have to install anything?</summary>
          <p>
            No. It runs in your browser, on a phone or a computer. Add it to your home
            screen if you'd like it to feel like an app.
          </p>
        </details>
      </section>

      <section className="land-final" data-reveal>
        <h2>Start with three things</h2>
        <p className="land-note">
          Don't inventory your kitchen — nobody finishes. Add the three that worry you.
          About forty seconds.
        </p>
        {cta}
      </section>

      <footer className="land-foot">
        <p>Larder · made in India, for Indian households</p>
      </footer>
    </div>
  );
}
